import { useState } from 'react'

import { useMe } from '@/providers'
import { useInfiniteQuery, useMutation } from '@api/hooks'
import NotificationCard from '@components/NotificationCard/NotificationCard'
import { Notification, NotificationReadStatus } from '@domain'
import { Box, Button, Popover, PopoverProps, Skeleton, Stack, Typography } from '@mui/material'
import NotificationsApi, { NotificationsKeys } from '@services/notifications.api'
import { useQueryClient } from '@tanstack/react-query'

const PAGE_SIZE = 10

type Props = {
	anchorEl: PopoverProps['anchorEl']
	onClose: () => void
}

export default function NotificationPopover({ anchorEl, onClose }: Props) {
	const [readStatus, setReadStatus] = useState(NotificationReadStatus.UNREAD)

	const me = useMe()
	const queryClient = useQueryClient()

	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
		queryKey: NotificationsKeys.list({ readStatus }),
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			NotificationsApi.list({ readStatus, page: pageParam, pageSize: PAGE_SIZE }),
		getNextPageParam: (lastPage) => {
			const { page, totalPages } = lastPage.pagination
			return page < totalPages ? page + 1 : null
		},
		enabled: Boolean(anchorEl),
	})

	const notifications = data?.pages.flatMap((page) => page.data) ?? []

	const invalidateNotifications = () => {
		queryClient.invalidateQueries({ queryKey: NotificationsKeys.all })
	}

	const markRead = useMutation({
		mutationFn: (id: Id) => NotificationsApi.markRead(id),
		onSuccess: invalidateNotifications,
	})

	const markUnread = useMutation({
		mutationFn: (id: Id) => NotificationsApi.markUnread(id),
		onSuccess: invalidateNotifications,
	})

	const markAllRead = useMutation({
		mutationFn: () => NotificationsApi.markAllRead(),
		onSuccess: invalidateNotifications,
	})

	const handleToggleFilter = () =>
		setReadStatus(
			readStatus === NotificationReadStatus.UNREAD
				? NotificationReadStatus.ALL
				: NotificationReadStatus.UNREAD
		)

	const handleMarkRead = (notification: Notification) => {
		markRead.mutate(notification.id)
	}

	const handleToggleRead = (notification: Notification) => {
		if (notification.read) {
			markUnread.mutate(notification.id)
		} else {
			markRead.mutate(notification.id)
		}
	}

	const handleMarkAllRead = () => {
		markAllRead.mutate()
	}

	return (
		<NotificationPopoverUI
			anchorEl={anchorEl}
			onClose={onClose}
			notifications={notifications}
			isLoading={isLoading}
			isFetchingNextPage={isFetchingNextPage}
			hasNextPage={hasNextPage}
			onLoadMore={() => fetchNextPage()}
			readStatus={readStatus}
			onToggleFilter={handleToggleFilter}
			onAction={handleMarkRead}
			onToggleRead={handleToggleRead}
			onMarkAllRead={handleMarkAllRead}
			markAllReadPending={markAllRead.isPending}
			markAllDisabled={!me?.unreadNotificationCount}
		/>
	)
}

type UIProps = Props & {
	notifications: Notification[]
	isLoading: boolean
	isFetchingNextPage: boolean
	hasNextPage: boolean
	onLoadMore: () => void
	readStatus: NotificationReadStatus
	onToggleFilter: () => void
	onAction: (notification: Notification) => void
	onToggleRead: (notification: Notification) => void
	onMarkAllRead: () => void
	markAllReadPending: boolean
	markAllDisabled: boolean
}

export function NotificationPopoverUI({
	anchorEl,
	onClose,
	notifications,
	isLoading,
	isFetchingNextPage,
	hasNextPage,
	onLoadMore,
	readStatus,
	onToggleFilter,
	onAction,
	onToggleRead,
	onMarkAllRead,
	markAllReadPending,
	markAllDisabled,
}: UIProps) {
	const isUnreadView = readStatus === NotificationReadStatus.UNREAD

	return (
		<Popover
			anchorEl={anchorEl}
			open={Boolean(anchorEl)}
			onClose={onClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			slotProps={{ paper: { sx: { mt: 2 } } }}
			data-testid="notification-popover"
		>
			<Stack sx={{ width: 480 }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
					<Typography variant="h6" color="primary">
						Notifications
					</Typography>
					<Button
						variant="text"
						size="small"
						color="secondary"
						onClick={onMarkAllRead}
						loading={markAllReadPending}
						disabled={markAllDisabled}
						data-testid="notifications-mark-all-read"
					>
						Mark all read
					</Button>
				</Stack>

				<Box
					sx={{
						maxHeight: 480,
						minHeight: 250,
						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
					}}
					data-testid="notifications-list"
				>
					<Stack spacing={1} sx={{ px: 1.5, py: 1, flexGrow: 1 }}>
						{isLoading && (
							<>
								<Skeleton variant="rounded" height={72} />
								<Skeleton variant="rounded" height={72} />
								<Skeleton variant="rounded" height={72} />
							</>
						)}

						{!isLoading &&
							notifications.map((notification) => (
								<NotificationCard
									key={notification.id}
									notification={notification}
									onAction={onAction}
									onToggleRead={onToggleRead}
								/>
							))}

						{!isLoading && !notifications.length && (
							<EmptyNotifications isUnreadView={isUnreadView} />
						)}

						{isFetchingNextPage && <Skeleton variant="rounded" height={72} />}

						{hasNextPage && (
							<Button
								variant="text"
								size="small"
								onClick={onLoadMore}
								disabled={isFetchingNextPage}
								data-testid="notifications-load-more"
							>
								show more
							</Button>
						)}
					</Stack>
				</Box>

				<Box sx={{ p: 2 }}>
					<Button
						variant="text"
						size="small"
						color="secondary"
						onClick={onToggleFilter}
						data-testid="notifications-filter-toggle"
					>
						{isUnreadView ? 'View all notifications' : 'View unread'}
					</Button>
				</Box>
			</Stack>
		</Popover>
	)
}

function EmptyNotifications({ isUnreadView }: { isUnreadView: boolean }) {
	return (
		<Typography
			variant="body2"
			color="text.secondary"
			align="center"
			sx={{
				flexGrow: 1,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				py: 4,
			}}
			data-testid="notifications-empty"
		>
			{isUnreadView ? "You're All Caught Up :)" : "You haven't received any notifications yet."}
		</Typography>
	)
}
